<template>
    <div>
        <transition name="search-fade">
            <div class="search-container tw-fixed tw-overflow-scroll tw-flex-col" v-show="full_screen_search_open" ref="searchContainer" @scroll="handleScroll">
                <div class="search-collapsed-holder tw-w-full tw-bg-white" v-if="hasSearched">
                    <div class="inner-holder" :class="{'extended-search': extended_search}"></div>
                </div>
                <div class="full-screen-search-wrapper tw-h-screen tw-w-screen tw-fixed tw-bg-white tw-left-0 tw-opacity-100" :class="[{'searched': hasSearched}, {'collapsed': search_collapsed}]" :style="{top: search_top_position + 'px'}"> <!-- :style="{top: search_top_position + 'px'}" -->
                    <div class="center-search tw-m-auto tw-opacity-100">
                        <div class="tw-flex tw-items-center tw-justify-center">
                            <i class="far fa-search tw-text-gray-300 tw-pr-6"></i>
                            <input type="text" class="tw-rounded-none tw-p-0 tw-m-0 tw-flex-1 tw-text-gray-700" name="query" ref="searchInput" v-model="query" :placeholder="translations['navigation.search_placeholder']" @click="closeExtendedSearch">
                        </div>
                        <!--<div class="general-button" @click="search">
                            <p>{{ translations['navigation.search'] }}</p>
                        </div>-->

                        <transition-group
                                appear
                                name="staggered-fade"
                                v-if="displaySearchOptions"
                                class="search-categories tw-flex tw-flex-wrap tw-mt-12 tw-mb-4"
                                tag="div"
                                :css="false"
                                @before-enter="beforeEnter"
                                @enter="enter"
                                @leave="leave">
                            <div class="search-category" :class="{'disabled': !filter.enabled}" v-for="(filter, index) in visibleFilters" :key="filter.name" :data-index="index" @click="toggleFilter(filter)">{{ translations[filter.trans_name] }}</div>
                        </transition-group>

                        <transition appear name="filters-slide-up">
                            <div class="search-filters tw-flex tw-items-center" v-if="displaySearchOptions">
                                <div class="search-filter tw-flex" @click="showDatePickers">
                                    <div class="tw-flex tw-items-center">
                                        <i class="fal fa-calendar"></i>
                                        {{ translations['fields.date'] }}
                                    </div>
                                    <div class="search-filter-dates tw-flex tw-mt-2 tw-ml-1" v-if="filter_start_date || filter_end_date">
                                        <p class="tw-border-b-2 tw-border-gray-100 tw-pb-1 tw-text-center" @click="toggleStartDateScheduler">{{ filterDate(filter_start_date) }}</p>
                                        <span class="tw-pl-2 tw-pr-2">{{ translations['fields.until'] }}</span>
                                        <p class="tw-border-b-2 tw-border-gray-100 tw-pb-1 tw-text-center" @click="toggleEndDateScheduler">{{ filterDate(filter_end_date) }}</p>
                                    </div>
                                </div>
                                <div class="search-filter search-filter-by tw-flex tw-relative tw-items-center" @click.self="showFilterDropdown" v-click-outside="closeFilterDropdown">
                                    <i class="fal fa-filter"></i>
                                    {{ translations[filter_by.trans_name] }}
                                    <div v-if="filter_by_dropdown_open" class="filter-by-card card-shadow tw-absolute tw-bg-white tw-rounded tw-overflow-hidden">
                                        <div class="first-dropdown-item tw-flex tw-items-center tw-p-4 tw-bg-gray-0" @click="setFilterTo(filter_by)">
                                            <i class="fal fa-filter"></i>
                                            <p>{{ translations[filter_by.trans_name] }}</p>
                                        </div>
                                        <p v-for="option in filter_by_options" v-if="option.name !== filter_by.name" @click="setFilterTo(option)">{{ translations[option.trans_name] }}</p>
                                    </div>
                                </div>
                            </div>
                        </transition>

                        <date-time-picker-wrapper v-if="start_scheduler_opened"
                                                  :date="filter_start_date"
                                                  @datePicked="setStartDate"
                                                  @closed="toggleStartDateScheduler">
                        </date-time-picker-wrapper>

                        <date-time-picker-wrapper v-if="end_scheduler_opened"
                                                  :date="filter_end_date"
                                                  @datePicked="setEndDate"
                                                  @closed="toggleEndDateScheduler">
                        </date-time-picker-wrapper>
                    </div>
                </div>
                <div class="search-results tw-flex-1 tw-w-screen tw-bg-gray-200">
                    <transition :name="animateSpinner">
                        <div class="spinner-container" v-if="searching">
                            <div class="spinner medium">
                                <div class="spinner-wrapper">
                                    <div class="rotator">
                                        <div class="inner-spin"></div>
                                        <div class="inner-spin"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>

                    <transition appear name="results-slide-down">
                        <div class="search-results-content tw-m-auto tw-mt-12" ref="searchResults" v-if="searchResultCount > 0" :class="[{'search-collapsed': search_collapsed}, {'extended-search': extended_search}]">
                            <div class="content-wrapper" v-for="filter in visibleFilters" :key="filter.name" v-if="search_results[filter.name.toLowerCase()] && search_results[filter.name.toLowerCase()].data && search_results[filter.name.toLowerCase()].data.length > 0 && filter.enabled && isNotExcludedFromExtendedSearch(filter.name.toLowerCase())">
                                <div class="tw-flex tw-text-gray-300 tw-items-center tw-pb-6 tw-cursor-pointer" v-if="extended_search" @click="closeExtendedSearch">
                                    <i class="fal fa-arrow-left tw-pr-2"></i>
                                    <p>{{ translations['search.back'] }}</p>
                                </div>
                                <div class="content-top tw-flex tw-justify-between tw-items-center">
                                    <div class="tw-flex">
                                        <h2>{{ translations[filter.trans_name] }}</h2>
                                    </div>
                                    <p class="content-load-more tw-cursor-pointer tw-text-gray-400 tw-text-sm tw-hidden md:tw-block" @click="search(filter.name.toLowerCase())" v-if="search_results[filter.name.toLowerCase()].meta.pagination.total > filter.default_display_amount && !extended_search">
                                        {{ translations['general.view_more'] }} {{ translations[filter.trans_name].toLowerCase() }}
                                    </p>
                                </div>
                                <component :is="'search' + singularName(filter.name)" :filter="filter_by.name" :items="search_results[filter.name.toLowerCase()].data" :extended="extended_search"></component>
                                <p class="content-load-more tw-cursor-pointer tw-text-gray-400 tw-text-sm tw-font-semibold tw-block md:tw-hidden" @click="search(filter.name.toLowerCase())" v-if="search_results[filter.name.toLowerCase()].meta.pagination.total > filter.default_display_amount && !extended_search">
                                    <i class="fa fa-arrow-right tw-pr-2"></i> {{ translations['general.view_more'] }} {{ translations[filter.trans_name].toLowerCase() }}
                                </p>
                            </div>
                        </div>
                        <div class="search-results-content tw-m-auto tw-mt-12" v-else-if="!searching">
                            <div class="content-wrapper">
                                <h3 class="tw-text-gray-600 tw-text-xl tw-font-bold">{{ translations['search.no_results'] }}</h3>
                                <p class="tw-text-gray-300 tw-mt-4">{{ translations['search.try_different'] }}</p>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import {mapState} from 'vuex';
    import Global from '../mixins/Global.js';
    import Velocity from 'velocity-animate';
    import VueScrollTo from 'vue-scrollto';
    import vClickOutside from 'v-click-outside'
    import moment from 'moment';
    import SearchConversation from '../components/search/SearchConversation.vue'
    import SearchBoard from '../components/search/SearchBoard.vue'
    import SearchUser from '../components/search/SearchUser.vue'
    import SearchLesson from '../components/search/SearchLesson.vue'
    import SearchCourse from '../components/search/SearchCourse.vue'

    require('pluralize');

    export default {
        mixins: [Global],
        name: 'FullScreenSearch',

        components: {
            searchconversation: SearchConversation,
            searchboard: SearchBoard,
            searchuser: SearchUser,
            searchlesson: SearchLesson,
            searchcourse: SearchCourse,
        },

        data() {
            return {
                searching: false,
                query: '',
                has_searched_before: false,
                time: 0,
                interval: null,
                filters: [
                    {
                        name: 'Users',
                        trans_name: "search.users",
                        enabled: true,
                        default_display_amount: 4,
                        plugin: 'community',
                    },
                    {
                        name: 'Conversations',
                        trans_name: "general.topics",
                        enabled: true,
                        default_display_amount: 5,
                        plugin: 'community',
                    },
                    {
                        name: 'Lessons',
                        trans_name: "search.content",
                        enabled: true,
                        default_display_amount: 3,
                        plugin: 'membership',
                    },
                    {
                        name: 'Boards',
                        trans_name: "navigation.boards",
                        enabled: true,
                        default_display_amount: 99,
                        plugin: 'community',
                    },
                    {
                        name: 'Courses',
                        trans_name: "membership.courses",
                        enabled: true,
                        default_display_amount: 3,
                        plugin: 'membership',
                    }
                ],
                search_collapsed: false,
                search_top_position: 70,
                window_scrolled: 0,
                last_scrolled: 0,
                extended_search: false,
                start_scheduler_opened: false,
                filter_start_date: false,
                end_scheduler_opened: false,
                filter_end_date: false,
                filter_by: {
                    name: 'Relevantie',
                    trans_name: 'search.relevance',
                },
                filter_by_options: [
                    {
                        name: 'Relevantie',
                        trans_name: 'search.relevance'
                    },
                    {
                        name: 'Nieuwste',
                        trans_name: 'search.newest'
                    },
                    {
                        name: 'Oudste',
                        trans_name: 'search.oldest'
                    }
                ],
                filter_by_dropdown_open: false,
            }
        },

        computed: {
            ...mapState([
                'full_screen_search_open',
                'search_results',
                'plugins',
            ]),

            displaySearchOptions() {
                if(this.search_collapsed) {
                    return false;
                }

                if(this.searchResultCount > 0 || this.searching || this.has_searched_before) {
                    return true;
                }

                return false;
            },

            hasSearched() {
                return this.searching || this.searchResultCount > 0 || this.has_searched_before;
            },

            searchResultCount() {
                let count = 0;
                let filters = Object.values(this.search_results);

                for (const filter of filters) {
                    if(filter.data) {
                        count += filter.data.length;
                    }
                }

                return count;
            },

            animateSpinner() {
                if(this.searching && this.extended_search) {
                    return 'animate-spinner';
                }

                return '';
            },

            visibleFilters() {
                return this.filters.filter((filter) => (this.plugins[filter.plugin] === 'installed' || (filter.plugin === 'community' && this.$settings('menu_boards') !== '')));
            },
        },

        created() {
            const keyHandler = (e) => {
                if (this.full_screen_search_open) {
                    if (e.key === `Escape`) {
                        this.toggleSearch();
                    } else if (e.key === `Enter`) {
                        e.preventDefault();

                        this.search();
                    }
                }
            };

            document.addEventListener(`keydown`, keyHandler);

            document.addEventListener(`scroll`, this.fakeScroll);

            this.$once(`hook:destroyed`, () => {
                document.removeEventListener(`keydown`, keyHandler);
            });

            this.window_scrolled = window.scrollY;
        },

        methods: {
            //handle scrolling on the fixed search bar
            fakeScroll() {
                if(!this.extended_search) {
                    let simulate_scroll_amount = window.scrollY - this.last_scrolled;
                    this.last_scrolled = window.scrollY;

                    this.$refs.searchContainer.scrollTop += simulate_scroll_amount;
                }
            },

            handleScroll() {
                if(!this.extended_search) {
                    this.positionSearchbar();
                } else {
                    //infinite scroll for loading more search results for a specific set of items
                    if ((this.$refs.searchContainer.offsetHeight + this.$refs.searchContainer.scrollTop + 100) >= this.$refs.searchContainer.scrollHeight && !this.searching && this.search_results[this.extended_search].meta.pagination.current_page < this.search_results[this.extended_search].meta.pagination.last_page) {
                        this.search(this.extended_search);
                    }
                }
            },

            toggleSearch() {
                //remove params from url if closing
                if(this.full_screen_search_open) {
                    let params = new URLSearchParams(location.search);

                    if(params.has('search')) {
                        params.delete('search');
                    }

                    if(params.has('order_by')) {
                        params.delete('order_by');
                    }

                    window.history.replaceState({}, '', `${location.pathname}?${params}`);
                }

                this.$store.commit('toggleFullScreenSearch');
            },

            search(model) {
                if (!this.searching) {
                    if (this.query !== '') {
                        //remove records if it is a new search
                        if(!model) {
                            this.$store.commit('clearSearchResults');
                        } else {
                            //scroll to top of container
                            this.$scrollTo(this.$refs.searchContainer, 500, {
                                container: this.$refs.searchContainer,
                                el: this.$refs.searchContainer
                            });
                        }

                        //start searching
                        this.searching = true;
                        this.has_searched_before = true;
                        this.interval = setInterval(this.incrementTime, 100);

                        //modify the url
                        let tab_params = new URLSearchParams(location.search);
                        tab_params.set('search', this.query);
                        tab_params.set('order_by', this.filter_by.name);
                        window.history.replaceState({}, '', `${location.pathname}?${tab_params}`);

                        //append params to the search for the search api
                        let params = new URLSearchParams();

                        params.append('query', this.query);

                        if(this.filter_start_date) {
                            params.append('min', this.filterDate(this.filter_start_date));
                        }

                        if(this.filter_end_date) {
                            params.append('max', this.filterDate(this.filter_end_date));
                        }

                        if(this.filter_by.name === 'Nieuwste') {
                            params.append('order_by', 'desc');
                        } else if(this.filter_by.name === 'Oudste') {
                            params.append('order_by', 'asc');
                        }

                        //if we have a model then we are loading more results for a specific set of items
                        if(model) {
                            params.append('model', model);
                            params.append('page', this.search_results[model].meta.pagination.current_page + 1);
                        }

                        let request = {
                            params: params
                        };

                        this.$store.dispatch('search', {
                            request: request,
                            search_more: !!model
                        }).then(() => {
                            this.showResults();

                            if(model) {
                                this.search_collapsed = true;
                                this.extended_search = model;
                                this.search_top_position = 70;
                                this.search_results[model].meta.pagination.current_page++;

                                //keep running the search for as long as there is more content to get and the user cant scroll yet (height of results smaller than screen height)
                                if(this.search_results[model].meta.pagination.current_page < this.search_results[model].meta.pagination.last_page) {
                                    setTimeout(() => {
                                        if(window.innerHeight > (this.$refs.searchResults.offsetHeight + this.$refs.searchResults.getBoundingClientRect().top)) {
                                            this.searching = false;
                                            this.search(model);
                                        }
                                    }, 100);
                                }
                            }
                        }, error => {
                            this.searching = false;
                            clearInterval(this.interval);
                            alert('Something went wrong during the search.');
                        });
                    }
                }
            },

            showResults() {
                if (this.time < 1000) {
                    setTimeout(() => {
                        this.searching = false;
                    }, 250);
                } else {
                    this.searching = false;
                }

                clearInterval(this.interval);
            },

            incrementTime() {
                this.time = parseInt(this.time) + 1;
            },

            beforeEnter: function (el) {
                el.style.opacity = 0;
            },
            enter: function (el, done) {
                let delay = el.dataset.index * 75;

                setTimeout(function () {
                    Velocity(
                        el,
                        { opacity: 1 },
                        { complete: done }
                    )
                }, delay)
            },
            leave: function (el, done) {
                let delay = el.dataset.index * 75;

                setTimeout(function () {
                    Velocity(
                        el,
                        { opacity: 0 },
                        { complete: done }
                    )
                }, delay)
            },

            toggleFilter(filter) {
                filter.enabled = !filter.enabled;
            },

            singularName(name) {
                return pluralize.singular(name).toLowerCase();
            },

            toggleStartDateScheduler() {
                this.start_scheduler_opened = !this.start_scheduler_opened;
            },

            setStartDate(date) {
                this.filter_start_date = date;
                this.search();
            },

            toggleEndDateScheduler() {
                this.end_scheduler_opened = !this.end_scheduler_opened;
            },

            setEndDate(date) {
                this.filter_end_date = date;
                this.search();
            },

            filterDate(date) {
                if(date) {
                    return moment(date).format('YYYY-MM-DD HH:mm:ss')
                }

                return '';
            },

            showDatePickers() {
                if(!this.filter_start_date && !this.filter_end_date) {
                    this.toggleStartDateScheduler();
                }
            },

            showFilterDropdown() {
                this.filter_by_dropdown_open = !this.filter_by_dropdown_open;
            },

            setFilterTo(filter) {
                if(filter.name !== this.filter_by.name) {
                    this.filter_by = filter;
                    this.filter_by_dropdown_open = false;
                    this.search();
                } else {
                    this.filter_by_dropdown_open = false;
                }
            },

            closeFilterDropdown() {
                this.filter_by_dropdown_open = false;
            },

            positionSearchbar() {
                let scrolled_from_top = this.$refs.searchContainer.scrollTop;

                //see if search needs to collapse
                if(scrolled_from_top > 220 && !this.search_collapsed) {
                    this.search_collapsed = true;
                } else if(scrolled_from_top < 50 && this.search_collapsed) {
                    this.search_collapsed = false;
                }

                //make the search bar move on scrolling up and down (this is necessary due to it being a fixed element)
                if(this.hasSearched && !this.search_collapsed) {
                    this.search_top_position = (70 - scrolled_from_top);
                } else {
                    this.search_top_position = 70;
                }
            },

            isNotExcludedFromExtendedSearch(filter) {
                if(!this.extended_search) {
                    return true;
                }

                return filter === this.extended_search;
            },

            closeExtendedSearch() {
                this.extended_search = false;
                this.$refs.searchContainer.scrollTop = 0;
                this.positionSearchbar();
            }
        },

        mounted() {
            let params = new URLSearchParams(location.search);

            if(params.has('search')) {
                this.$store.commit('toggleFullScreenSearch');
                this.query = params.get('search');

                if(params.has('order_by')) {
                    switch (params.get('order_by')) {
                        case 'Nieuwste':
                            this.filter_by = {
                                name: 'Nieuwste',
                                trans_name: 'search.newest'
                            };
                            break;
                        case 'Oudste':
                            this.filter_by = {
                                name: 'Oudste',
                                trans_name: 'search.oldest'
                            };
                            break;
                    }
                }

                this.search();
            }
        },

        watch: {
            full_screen_search_open(bool) {
                // focus the search input
                let el = this.$refs.searchInput;

                this.$nextTick(() => {
                    el.focus();
                });

                if (bool === true) {
                    // if we already have search params, start search
                    let params = new URLSearchParams(location.search);

                    if(params.has('query')) {
                        this.query = params.get('query');
                        this.search();
                    }
                }
            }
        },

        directives: {
            clickOutside: vClickOutside.directive
        }
    }
</script>

<style lang="scss" scoped>
    .search-results {
        .search-results-content {
            @apply tw-m-auto tw-mt-12;
            max-width: 779px;

            &.extended-search {
                @apply tw-mt-24;

                @media(max-width: 767px) {
                    @apply tw-mt-6;
                }
            }

            .content-wrapper {
                @apply tw-pb-6 tw-mb-10 tw-border-b;
                border-color: #e3e4e8;

                &:last-of-type {
                    @apply tw-border-0;
                }

                .content-top {
                    @apply tw-mb-7 tw-flex;

                    h2, span {
                        @apply tw-text-2xl tw-leading-none;
                    }

                    h2 {
                        @apply tw-font-semibold tw-text-gray-600;
                    }

                    span {
                        @apply tw-pl-2 tw-text-gray-400;
                    }

                    .content-load-more {
                        &:hover {
                            @apply tw-opacity-50;
                        }
                    }
                }
            }

            @media(max-width: 767px) {
                @apply tw-pl-6 tw-pr-6;
            }
        }

        .spinner-container {
            position: fixed;
            display: flex;
            width: 100vw;
            height: 100vh;
            top: 0;
            left: 0;
            justify-content: center;
            align-items: center;
            padding-top: 124px;
            z-index: 1;
        }

        .spinner-wrapper {
            &:after {
                background: #f7f7f7;
            }
        }

        .inner-spin {
            background: #ffffff;
        }
    }

    .search-container {
        top: 70px;
        z-index: 98;
        display: flex; //dont add flex tailwind class, it bugs for some reason
        height: calc(100vh - 70px);

        .search-collapsed-holder {
            .inner-holder {
                height: 220px; //needs to be a seperate child due to some weird flex bug (https://github.com/philipwalton/flexbugs/issues/173 ?)
                transition: height 0.2s ease;

                &.extended-search {
                    height: 0;
                }
            }

            @media(max-width: 767px) {
                display: none !important;
            }
        }

        @media(max-width: 1100px) {
            z-index: 9997;
        }

        @media(max-width: 767px) {
            top: 64px;
            height: calc(100% - 64px);
        }
    }

    .full-screen-search-wrapper {
        top: 70px;
        z-index: 97;
        transition: height 0.2s ease-in-out;

        .center-search {
            max-width: 779px;
            margin-top: 150px;
            transition: opacity 0.2s ease-in-out, margin 0.2s ease-in-out;

            i {
                font-size: 35px;
                transition: font-size 0.2s ease-in-out;
            }

            input {
                border: 0;
                font-size: 26px;
                transition: font-size 0.2s ease-in-out;
                background: none;
                margin-top: 0.15rem;

                &::placeholder {
                    @apply tw-text-gray-500;
                }
            }

            .search-categories {
                .search-category{
                    @apply tw-text-gray-600 tw-mr-3 tw-py-2 tw-px-5 tw-rounded tw-text-center tw-text-sm tw-font-semibold tw-cursor-pointer;
                    min-width: 95px;
                    border: 2px solid #7d7d7d;

                    &:hover {
                        opacity: 0.75 !important;
                    }

                    &.disabled {
                        opacity: 0.5 !important;
                    }

                    @media(max-width: 767px) {
                        @apply tw-text-xs tw-mr-2 tw-px-2;
                        min-width: 75px;
                    }
                }
            }

            .search-filters {
                .search-filter {
                    @apply tw-text-gray-300 tw-text-sm tw-mr-4 tw-cursor-pointer;

                    i {
                        @apply tw-pr-1 tw-text-sm;

                        &:hover {
                            @apply tw-opacity-75;
                        }
                    }
                }

                .search-filter-dates {
                    p {
                        min-width: 7rem;
                    }
                }

                .search-filter-by {
                    .filter-by-card {
                        width: 150px;
                        left: -1rem;
                        top: -1rem;

                        p {
                            @apply tw-p-4 tw-pl-8;
                        }

                        .first-dropdown-item {
                            p {
                                @apply tw-p-0;
                            }
                        }

                        p, .first-dropdown-item {
                            &:hover {
                                @apply tw-bg-gray-0;
                            }
                        }
                    }
                }
            }

            @media(max-width: 767px) {
                @apply tw-pl-6 tw-pr-6;
                margin-top: 25px;

                i {
                    font-size: 25px;
                }

                input {
                    font-size: 17px;
                    margin-top: 0.35rem;
                }

                .search-filters {
                    .search-filter {
                        i {
                            @apply tw-text-sm;
                        }
                    }
                }
            }
        }

        &.searched {
            //position: relative;
            //top: 0px;
            height: 220px;
            //padding-bottom: 29px;

            .center-search {
                margin-top: 45px;

                i {
                    font-size: 30px;
                }

                input {
                    font-size: 24px;
                }
            }
        }

        &.collapsed {
            @apply tw-shadow-light;
            top: 70px;
            transition: top 0.2s ease-in-out;
            height: 54px;

            .center-search {
                margin-top: 1.1rem;

                i {
                    @apply tw-text-lg;
                    transition: none;
                }

                input {
                    @apply tw-text-lg;
                    transition: none;
                }
            }
        }

        @media(max-width: 767px) {
            position: relative;
            height: auto;
            margin-top: 0px;
            padding-bottom: 20px;
            top: auto !important;
        }
    }

    //full screen search slide
    .search-fade-enter-active {
        transition: top .3s ease-in-out, opacity .3s ease-in-out;

        .full-screen-search-wrapper {
            transition: top .3s ease-in-out, opacity .3s ease-in-out;
        }
    }

    .search-fade-leave-active {
        transition: top .2s ease-in-out, opacity .2s ease-in-out;

        .full-screen-search-wrapper {
            transition: top .2s ease-in-out, opacity .2s ease-in-out;
        }
    }

    .search-fade-enter, .search-fade-leave-to {
        top: -100vh !important;
        opacity: 0;

        .full-screen-search-wrapper {
            top: -100vh !important;
            opacity: 0;
        }
    }

    .search-fade-enter-to, .search-fade-leave {
        top: 70px !important;
        opacity: 1;

        .full-screen-search-wrapper {
            top: 70px !important;
            opacity: 1;
        }

        @media(max-width: 767px) {
            top: 30px !important;

            .full-screen-search-wrapper {
                top: 30px !important;
            }
        }
    }

    //filters slide up
    .filters-slide-up-enter-active {
        transition: transform 0.3s ease-out;
    }

    .filters-slide-up-enter {
        transform: translateY(25px);
    }

    .filters-slide-up-enter-to {
        transform: translateY(0px);
    }

    //results slide up
    .results-slide-down-enter-active {
        transition: transform 0.3s ease-out;
    }

    .results-slide-down-enter {
        transform: translateY(-25px);
    }

    .results-slide-down-enter-to {
        transform: translateY(0px);
    }

    //animation for spinner
    .animate-spinner-enter-active {
        transition: opacity .5s ease-in-out, background .5s ease-in-out;
    }

    .animate-spinner-leave-active {
        transition: opacity .5s ease-in-out, background .5s ease-in-out;
    }

    .animate-spinner-enter, .animate-spinner-leave-to {
        background: rgba(255, 255, 255, 0);
        opacity: 0;
    }

    .animate-spinner-enter-to, .animate-spinner-leave {
        background: rgba(255, 255, 255, 0.5);
        opacity: 1;
    }
</style>
